import {useCallback, useRef, useState} from "react";
import {BASE_URL, getAuthHeader} from "../../config";

// Opens one of the participant's own documents (POST /my-case/document).
// The server re-checks that the id belongs to the signed-in participant and
// is in a participant-visible category, so this hook just moves bytes.
//
// View-first UX (Marco, 08-17): PDFs and images open full-screen in a new
// tab in the browser's own viewer, so the person SEES their document instead
// of a silent save to Downloads (which this population loses track of). The
// native viewer still offers save/share. Types the browser can't render
// (docx etc.) fall back to a normal download.
//
// Popup-blocker contract: the viewer tab must be window.open()ed
// SYNCHRONOUSLY inside the tap, before any await, then navigated to the
// blob once the fetch lands. Opening after the await gets blocked on iOS.
//
// Per-document state so each row can show its own spinner/error:
//   states[document_id] = "loading" | "error" (absent = idle)

// ~95% of prod documents are PDFs/images (census 08-17); these render
// natively in every mobile + desktop browser we support. Image types are an
// explicit allowlist: SVG is NOT viewable because an SVG document can run
// script, and a blob URL executes on the app origin (where remember-me
// Cognito tokens live in localStorage). SVGs download instead.
const VIEWABLE_MIME =
  /^(application\/pdf|image\/(png|jpe?g|gif|webp)|text\/plain)(\s*;|$)/i;
const VIEWABLE_EXT = /\.(pdf|png|jpe?g|gif|webp|txt)$/i;

export const isViewableDoc = (doc) =>
  VIEWABLE_MIME.test(doc?.mime_type || "") ||
  VIEWABLE_EXT.test(doc?.file_name || doc?.name || "");

// Painted into the claimed tab immediately, so the person never stares at
// about:blank while the file fetches (measured ~3s on good wifi; longer on
// participants' connections). Plain language, DHW blue, self-contained.
const loadingPage = (
  name,
) => `<!doctype html><html><head><title>Opening…</title>
<meta name="viewport" content="width=device-width, initial-scale=1">
<style>body{margin:0;font-family:system-ui,sans-serif;background:#fff;display:flex;min-height:100vh;align-items:center;justify-content:center}
.box{text-align:center;color:#191919;padding:24px}
.spin{width:44px;height:44px;border:4px solid #EEF1FB;border-top-color:#001489;border-radius:50%;margin:0 auto 16px;animation:r 0.9s linear infinite}
@keyframes r{to{transform:rotate(360deg)}}
h1{font-size:1.1rem;margin:0 0 6px}p{font-size:.95rem;color:#555;margin:0}</style></head>
<body><div class="box"><div class="spin"></div><h1>Opening your document</h1><p>${name.replace(/[<>&]/g, "")}<br>This can take a few seconds.</p></div></body></html>`;

// Session cache: doc id -> object URL. Re-opening a document is instant and
// costs no second fetch; URLs live until the portal tab closes.
const blobCache = new Map();
export const __clearBlobCacheForTests = () => blobCache.clear();

const useDocumentOpen = (customerId) => {
  const [states, setStates] = useState({});
  const inFlight = useRef(new Set());

  const setDocState = (id, value) =>
    setStates((prev) => {
      const next = {...prev};
      if (value) next[id] = value;
      else delete next[id];
      return next;
    });

  const openDocument = useCallback(
    async (doc) => {
      const id = doc?.document_id;
      if (!id || !customerId || inFlight.current.has(id)) return;
      inFlight.current.add(id);
      setDocState(id, "loading");
      const viewable = isViewableDoc(doc);
      const cached = blobCache.get(id);
      // Claim the viewer tab inside the gesture (see contract above) and
      // paint the loading screen into it right away — never about:blank.
      const viewerTab = viewable && !cached ? window.open("", "_blank") : null;
      if (viewerTab) {
        // Keep our handle on the tab but break its backlink to this page, so
        // nothing rendered in it can navigate or script the portal tab.
        viewerTab.opener = null;
        try {
          viewerTab.document.write(
            loadingPage(doc.name || doc.file_name || "Your document"),
          );
          viewerTab.document.close();
        } catch {
          /* cross-origin edge: tab still opens, just blank */
        }
      }
      try {
        let url = cached;
        // The mime/filename metadata is only the pre-fetch GUESS that claims
        // the viewer tab; the browser renders the SERVED type. The blob's
        // own type re-decides below, so conflicting metadata (an SVG named
        // .png) downloads instead of rendering. Cached URLs already passed.
        let view = viewable;
        if (!url) {
          const Authorization = await getAuthHeader();
          const response = await fetch(`${BASE_URL}/my-case/document`, {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              ...(Authorization ? {Authorization} : {}),
            },
            body: JSON.stringify({customer_id: customerId, document_id: id}),
          });
          if (!response.ok) throw new Error(`HTTP ${response.status}`);
          const blob = await response.blob();
          view = viewable && VIEWABLE_MIME.test(blob.type || "");
          url = URL.createObjectURL(blob);
          if (view) blobCache.set(id, url); // instant re-opens
        }
        if (view) {
          // Cached: open directly in the gesture. Fresh: navigate the
          // already-claimed loading tab.
          if (viewerTab) viewerTab.location = url;
          else window.open(url, "_blank", "noopener");
        } else {
          // A tab claimed on a wrong guess closes; the file downloads.
          if (viewerTab) viewerTab.close();
          const link = document.createElement("a");
          link.href = url;
          link.download = doc.file_name || doc.name || "document";
          document.body.appendChild(link);
          link.click();
          link.remove();
          setTimeout(() => URL.revokeObjectURL(url), 60_000);
        }
        setDocState(id, null);
      } catch (err) {
        console.error(`Document open failed (${id}):`, err);
        if (viewerTab) viewerTab.close();
        setDocState(id, "error");
      } finally {
        inFlight.current.delete(id);
      }
    },
    [customerId],
  );

  return {openDocument, docStates: states};
};

export default useDocumentOpen;
